import React, { useCallback, useEffect, useMemo, useState } from "react";
import { emitToast } from "./Toast";
import { HISTORY_MONTHS_BACK, hm, monthsAgoYm, todayYmd } from "./dateUtil";
import DatePickerCalendar from "./DatePickerCalendar";
import ErrorBar from "./ErrorBar";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 時間訂正 申請フォーム本体（社員本人・セッション認証）。Porcelain テーマ。
// 申請ハブ(ApplyHubPage)のタブに載る部品として TimeCorrectionBody を named export する。
// 申請履歴は種別横断の共通一覧(ApplyHistory)へ集約したため、ここは「新規申請フォーム」だけを持つ。
// 送信に成功したら onChanged() を呼び、親（ApplyHubPage）に共通の申請履歴を再取得させる。
// 経営者の承認は別ルート(/employee/time-correction-approval)。旧 /employee/time-correction はハブへリダイレクト。
//
// 【日付起点】まず「対象の日」をカレンダーで選ぶ。
//  ・その日に打刻がある → 既存値を表示して訂正（attendance_id を送る）。
//  ・打刻が無い日（丸一日押し忘れ等）→ 空欄で新規登録（date を送る。承認時に打刻が作られる）。
// これで「打刻レコードが無いと選択肢に出ない」問題を解消する。
const API_URL = process.env.REACT_APP_API_URL || "";

// 打刻レコードの対象日（YYYY-MM-DD）。出勤があれば出勤日、無ければ退勤日。
const dateOf = (a) => {
  if (a.clock_in) return a.clock_in.slice(0, 10);
  if (a.clock_out) return a.clock_out.slice(0, 10);
  return null;
};

export function TimeCorrectionBody({ onChanged }) {
  const [attendances, setAttendances] = useState([]);
  // 選択中の日（YYYY-MM-DD）と、その日の出退勤時刻（HH:MM）。
  const [selectedDate, setSelectedDate] = useState("");
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const today = todayYmd();

  // フォームの初期表示に必要な「自分の打刻一覧」だけ取得する（申請履歴は共通一覧が持つ）。
  const fetchAttendances = useCallback(async () => {
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/attendances/`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (res.status === 403) { setError("社員プロフィールがありません。管理者にお問い合わせください。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      setAttendances(data.results || []);
    } catch (e) {
      setError("データの取得に失敗しました。");
    }
  }, []);

  useEffect(() => { fetchAttendances(); }, [fetchAttendances]);

  // 日付 → 打刻レコード（その日に既に打刻があるか判定するため）。
  const attByDate = useMemo(() => {
    const map = {};
    attendances.forEach((a) => {
      const ds = dateOf(a);
      if (ds && !map[ds]) map[ds] = a;
    });
    return map;
  }, [attendances]);

  const selectedAtt = selectedDate ? attByDate[selectedDate] : null;
  const isNew = !!selectedDate && !selectedAtt; // 打刻が無い日＝新規登録

  // 送信可否（他の申請フォームと同様、条件を満たすまで申請ボタンは非活性）。
  const origInTime = selectedAtt && selectedAtt.clock_in ? hm(selectedAtt.clock_in) : "";
  const origOutTime = selectedAtt && selectedAtt.clock_out ? hm(selectedAtt.clock_out) : "";
  const canSubmit = !!selectedDate && ((!!inTime && inTime !== origInTime) || (!!outTime && outTime !== origOutTime));

  // 日付を選んだら、その日の打刻値を初期表示（無ければ空欄）。※hm(null)は1970年を返すのでガード必須。
  const onPickDate = (ds) => {
    setSelectedDate(ds);
    const a = attByDate[ds];
    setInTime(a && a.clock_in ? hm(a.clock_in) : "");
    setOutTime(a && a.clock_out ? hm(a.clock_out) : "");
  };

  const resetForm = () => { setSelectedDate(""); setInTime(""); setOutTime(""); setReason(""); };

  const submit = async () => {
    if (!selectedDate) { setError("対象の日を選んでください。"); return; }
    // 既存打刻の元の値と比べ、変更（または新規入力）した項目だけ申請する。
    const origIn = selectedAtt && selectedAtt.clock_in ? hm(selectedAtt.clock_in) : "";
    const origOut = selectedAtt && selectedAtt.clock_out ? hm(selectedAtt.clock_out) : "";
    const changes = [];
    if (inTime && inTime !== origIn) changes.push({ field: "clock_in", time: inTime });
    if (outTime && outTime !== origOut) changes.push({ field: "clock_out", time: outTime });
    if (changes.length === 0) {
      setError(isNew ? "出勤または退勤の時刻を入力してください。" : "出勤または退勤の時刻を変更してください。");
      return;
    }
    setSubmitting(true); setError("");
    try {
      // 出勤・退勤の両方を入れた場合は2件の申請を送る（既存の1件=1項目の仕組みに合わせる）。
      for (const c of changes) {
        const body = { field: c.field, new_value: `${selectedDate}T${c.time}`, reason };
        // 打刻がある日は attendance_id で訂正、無い日は date で新規登録。
        if (selectedAtt) body.attendance_id = Number(selectedAtt.id);
        else body.date = selectedDate;
        const res = await fetch(`${API_URL}/api/employee/time-correction/`, {
          method: "POST", credentials: "include",
          headers: jsonHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) { setError(await apiErrorMessage(res, "申請の送信に失敗しました。")); return; }
      }
      resetForm();
      emitToast(isNew ? "打刻の登録を申請しました。承認をお待ちください。" : "訂正を申請しました。承認をお待ちください。");
      // 送信後に打刻一覧も取り直す（新規登録日が反映される）＋共通の申請履歴を再取得。
      await fetchAttendances();
      if (onChanged) onChanged();
    } catch (e) {
      setError("申請の送信に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      <h2 className="mp-h2">新規申請</h2>
      <div className="mp-panel" style={{ marginBottom: 28 }}>
        <div className="mp-field">
          <label className="mp-flabel">対象の日</label>
          <DatePickerCalendar
            value={selectedDate}
            onChange={onPickDate}
            isDisabled={(ds) => ds > today}
            minYm={monthsAgoYm(HISTORY_MONTHS_BACK)}
            initialYm={monthsAgoYm(0)}
            dayMarker={(ds) => (attByDate[ds] ? "record" : null)}
            placeholder="日付を選ぶ"
          />
        </div>

        {/* 日付を選ぶまでは出勤/退勤/理由は出さない（対象日が決まってから入力）。申請ボタンは常時表示・非活性。
            打刻あり/なしはカレンダーの緑ドットと時刻のプリフィル有無で分かるため、モード表示は置かない。 */}
        {selectedDate ? (
          <>
            <div className="mp-formrow">
              <div className="mp-field">
                <label className="mp-flabel">出勤</label>
                <input className="mp-input" type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} />
              </div>
              <div className="mp-field">
                <label className="mp-flabel">退勤</label>
                <input className="mp-input" type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} />
              </div>
            </div>
            <div className="mp-field">
              <label className="mp-flabel">理由</label>
              <textarea className="mp-textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </>
        ) : null}
        <button className="mp-btn mp-primary" disabled={submitting || !canSubmit} onClick={submit}>{submitting ? "送信中…" : "申請する"}</button>
      </div>
    </>
  );
}
